import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useState } from 'react'
import type * as React from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useResolvedImages } from '@/api/produktbilder'
import { ProductPhoto } from '@/components/ProductPhoto'
import {
  getOrder,
  getOrderHistory,
  getOrderStockHistory,
  addOrderComment,
  type Order,
  type OrderAddress,
  type OrderLine,
  type StuecklistenGrund,
} from '../../api/orders'
import { ApiError } from '../../api/client'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { PortalBadge } from '@/components/PortalBadge'
import { OrderStatusFlags } from '@/components/OrderStatusFlags'
import { Panel } from '@/components/Panel'
import { Check, ChevronLeft, Copy, Printer } from 'lucide-react'

function drNumber(number: string): string {
  return number.replace(/^DR_/, '').replace(/_\d{4}_\d{2}$/, '')
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(+d)) return '—'
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function fmtTag(s: string | null | undefined): string {
  if (!s) return ''
  const d = new Date(s)
  if (Number.isNaN(+d)) return ''
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' })
}

/** Kurzer Zeitpunkt "21.10. 15:15" in Berlin-Zeit fuer die Statusliste. */
function fmtKurz(s: string): string {
  const d = new Date(s)
  if (Number.isNaN(+d)) return ''
  const t = Object.fromEntries(
    new Intl.DateTimeFormat('de-DE', {
      timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(d).map((p) => [p.type, p.value]),
  )
  return `${t.day}.${t.month}. ${t.hour}:${t.minute}`
}

/**
 * Anzeigeheuristik nur fuer die Statusliste: DR liefert dort meist nur das Datum, gespeichert
 * als 00:00:00 UTC (in Berlin "02:00"). Genau dieser Zeitpunkt zeigt nur "22.10.".
 */
function fmtStatus(s: string): string {
  const d = new Date(s)
  if (Number.isNaN(+d)) return ''
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) {
    const t = Object.fromEntries(
      new Intl.DateTimeFormat('de-DE', { timeZone: 'UTC', day: '2-digit', month: '2-digit' })
        .formatToParts(d).map((p) => [p.type, p.value]),
    )
    return `${t.day}.${t.month}.`
  }
  return fmtKurz(s)
}

/** Name zum Vergleich Rechnung/Lieferung: Vorname, Nachname, Firma — getrimmt, klein. */
function namensSchluessel(a: OrderAddress | null): string {
  if (!a) return ''
  return [a.firstname, a.lastname, a.company].map((v) => (v ?? '').trim().toLowerCase()).join('|')
}

/** Nur echte https-Links verlinken; alles andere bleibt Text. */
function httpsLink(url: string | undefined): string | null {
  if (!url) return null
  try {
    return new URL(url).protocol === 'https:' ? url : null
  } catch {
    return null
  }
}

const STUECKLISTEN_GRUND: Record<StuecklistenGrund, string> = {
  lagerartikel_fehlt: 'Lagerartikel fehlt',
  kein_kopf: 'Grundartikel fehlt',
  keine_sternnummer: 'Sternnummer fehlt',
  komponente_fehlt: 'Stücklisten-Teil fehlt',
  faktor_ungueltig: 'Stücklisten-Faktor ungültig',
  verschachtelt: 'Set im Set, nicht aufgelöst',
  menge_ungueltig: 'Menge ungültig',
}

function fmtEUR(n: string | number | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—'
  const num = typeof n === 'number' ? n : Number(n)
  if (Number.isNaN(num)) return '—'
  return num.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
}

// Backend liefert Mengen als numeric-String ("2.000") — als Stueckzahl ohne Nullen zeigen.
function fmtQty(q: string | number): string {
  const num = Number(q)
  if (Number.isNaN(num)) return String(q)
  return num.toLocaleString('de-DE', { maximumFractionDigits: 3 })
}

// Zahlungsarten laut Schema-Kommentar orders.payment_method_id
const PAYMENT_METHODS: Record<string, string> = { '1': 'Vorkasse', '2': 'PayPal', '3': 'Rechnung' }

interface StatusFlagDef {
  key: keyof Order['status']
  atKey: keyof Order['status']
  label: string
  danger?: boolean
}

const STATUS_FLAGS: StatusFlagDef[] = [
  { key: 'is_initial_contact', atKey: 'is_initial_contact_at', label: 'Erstkontakt' },
  { key: 'is_checkout', atKey: 'is_checkout_at', label: 'Checkout' },
  { key: 'is_paid', atKey: 'is_paid_at', label: 'Bezahlt' },
  { key: 'is_packed', atKey: 'is_packed_at', label: 'Kommissioniert' },
  { key: 'is_shipped', atKey: 'is_shipped_at', label: 'Versendet' },
  { key: 'is_arrived', atKey: 'is_arrived_at', label: 'Zugestellt' },
  { key: 'is_cancelled', atKey: 'is_cancelled_at', label: 'Storniert', danger: true },
  { key: 'is_parked', atKey: 'is_parked_at', label: 'Geparkt' },
]

const HISTORY_TYPES: Record<string, string> = {
  status: 'Status',
  comment: 'Kommentar',
  system: 'System',
  shipping: 'Versand',
}

type DetailTab = 'auftrag' | 'rohdaten'

function addressLines(a: OrderAddress): string[] {
  return [
    [a.salutation, a.firstname, a.lastname].filter(Boolean).join(' '),
    a.company ?? '',
    [a.street, a.street_number].filter(Boolean).join(' '),
    a.address_line_2 ?? '',
    [a.zip, a.city].filter(Boolean).join(' '),
    [a.state, a.country?.toUpperCase()].filter(Boolean).join(' · '),
  ].filter((l) => l.trim() !== '')
}

// Zeilen der Adresstabelle in der Reihenfolge von DreamRobot (Name, Name 2, Strasse, … Land).
const ADRESS_ZEILEN: Array<{ label: string; wert: (a: OrderAddress) => string }> = [
  { label: 'Name', wert: (a) => [a.salutation, a.firstname, a.lastname].filter(Boolean).join(' ') },
  { label: 'Name 2', wert: (a) => a.company ?? '' },
  { label: 'Straße', wert: (a) => [a.street, a.street_number].filter(Boolean).join(' ') },
  { label: 'Straße 2', wert: (a) => a.address_line_2 ?? '' },
  { label: 'PLZ', wert: (a) => a.zip ?? '' },
  { label: 'Ort', wert: (a) => a.city ?? '' },
  { label: 'Region', wert: (a) => a.state ?? '' },
  { label: 'Land', wert: (a) => a.country?.toUpperCase() ?? '' },
]

// Adresse zum Einfuegen in Versand- oder Mailprogramme: eine Zeile je Angabe, PLZ und Ort zusammen.
function adresseAlsText(a: OrderAddress): string {
  return [
    [a.salutation, a.firstname, a.lastname].filter(Boolean).join(' '),
    a.company ?? '',
    [a.street, a.street_number].filter(Boolean).join(' '),
    a.address_line_2 ?? '',
    [a.zip, a.city].filter(Boolean).join(' '),
    a.state ?? '',
    a.country?.toUpperCase() ?? '',
  ].filter((l) => l.trim() !== '').join('\n')
}

// Link zum Portal wie in DreamRobot: eBay-Angebot ueber die Artikelnummer (Portal-SKU),
// Amazon-Bestellung in Seller Central ueber die Bestellnummer (FBA haengt „_T…“ an).
function portalLink(platform: string | null, externalOrderId: string | null, sku: string): { href: string; title: string } | null {
  if (/ebay/i.test(platform ?? '') && /^\d{9,15}$/.test(sku)) {
    return { href: `https://www.ebay.de/itm/${sku}`, title: 'Angebot bei eBay öffnen' }
  }
  const amazonNr = /amazon/i.test(platform ?? '') ? externalOrderId?.match(/^(\d{3}-\d{7}-\d{7})(?:_T\w+)?$/)?.[1] : undefined
  if (amazonNr) {
    return { href: `https://sellercentral.amazon.de/orders-v3/order/${amazonNr}`, title: 'Bestellung in Amazon Seller Central öffnen' }
  }
  return null
}

// Kleiner Kopierknopf; zeigt kurz einen Haken. Im Druck ausgeblendet.
function KopierKnopf({ text, label }: { text: string; label: string }) {
  const [kopiert, setKopiert] = useState(false)
  return (
    <button
      type="button"
      aria-label={`${label} kopieren`}
      title={`${label} kopieren`}
      onClick={(e) => {
        e.stopPropagation()
        void navigator.clipboard?.writeText(text).then(() => {
          setKopiert(true)
          window.setTimeout(() => setKopiert(false), 1200)
        }, () => undefined)
      }}
      className="inline-flex shrink-0 items-center justify-center rounded p-0.5 align-middle text-ink-2 hover:text-ink hover:bg-panel-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary print:hidden"
    >
      {kopiert ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
    </button>
  )
}

// Abschnitt mit kleiner Ueberschrift; aria-label macht ihn fuer Tests und Screenreader auffindbar.
function Block({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section aria-label={title} className={`rounded-md border border-line bg-panel px-3 py-2 ${className}`}>
      <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1.5">{title}</h3>
      {children}
    </section>
  )
}

// Feld wie in der DreamRobot-Maske: Beschriftung, darunter der Wert in einem Kasten (nur lesen).
// Mit kopieren wird ein nicht leerer Text-Wert ueber einen kleinen Knopf rechts im Kasten kopierbar.
function Feld({ label, children, mono = false, kopieren = false, className = 'min-w-0' }: {
  label: string
  children?: React.ReactNode
  mono?: boolean
  kopieren?: boolean
  className?: string
}) {
  const leer = children === undefined || children === null || children === ''
  const kopierText = kopieren && !leer && typeof children === 'string' ? children : null
  return (
    <div className={className}>
      <div className="text-[11px] text-ink-2 leading-tight mb-0.5 whitespace-nowrap">{label}</div>
      <div
        className={`flex items-start gap-1 min-h-7 rounded border border-line bg-panel-2/50 px-2 py-1 text-[13px] leading-snug text-ink ${mono ? 'font-mono' : ''}`}
      >
        {/* mit Knopf darf ein langer Wert (z. B. FBA-Portal-Nr.) umbrechen, statt unter den Knopf zu laufen */}
        <div className={`min-w-0 flex-1 select-all ${kopierText ? 'whitespace-normal [overflow-wrap:anywhere]' : 'break-words'}`}>
          {leer ? <span className="text-muted">—</span> : children}
        </div>
        {kopierText && <KopierKnopf text={kopierText} label={label} />}
      </div>
    </div>
  )
}

// Zelle der Adresstabelle: Wert zum Markieren, daneben ein Kopierknopf, wenn etwas drinsteht.
function AdressZelle({ wert, label, fett, className = '' }: { wert: string; label: string; fett: boolean; className?: string }) {
  if (!wert) return <td className={`py-0.5 ${className}`}><span className="text-muted">—</span></td>
  return (
    <td className={`py-0.5 ${className}`}>
      <span className="inline-flex items-start gap-1">
        <span className={`select-all ${fett ? 'font-semibold' : ''}`}>{wert}</span>
        <KopierKnopf text={wert} label={label} />
      </span>
    </td>
  )
}

// Freitext-Kasten (Infos / Kommentare) mit fester Mindesthoehe wie die Textfelder in DreamRobot.
function Textkasten({ children, leer }: { children: React.ReactNode; leer: boolean }) {
  return (
    <div className="min-h-16 rounded border border-line bg-panel-2/50 px-2 py-1 text-[13px] leading-snug text-ink whitespace-pre-line">
      {leer ? <span className="text-muted">—</span> : children}
    </div>
  )
}

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  // Kam der Nutzer aus der Auftragsliste, steht dort deren Adresse (Ansicht, Suche, Seite).
  const liste = (location.state as { liste?: unknown } | null)?.liste
  const zurListe = () =>
    navigate(typeof liste === 'string' && liste.startsWith('/orders') ? liste : '/orders')
  const queryClient = useQueryClient()
  const [comment, setComment] = useState('')
  const [activeTab, setActiveTab] = useState<DetailTab>('auftrag')

  const { data: order, isLoading, isError, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(id!),
    enabled: !!id,
  })

  const historyQuery = useQuery({
    queryKey: ['order-history', id],
    queryFn: () => getOrderHistory(id!),
    enabled: !!id,
  })
  const history = historyQuery.data
  const lagerQuery = useQuery({
    queryKey: ['order-stock-history', id],
    queryFn: () => getOrderStockHistory(id!),
    enabled: !!id,
  })
  const bildSku = (line: OrderLine) => line.lager_sku ?? line.stock_sku ?? line.sku
  const photos = useResolvedImages((order?.lines ?? []).map(bildSku))
  const photosBySku = new Map(photos.data?.results.map(row => [row.sku, row]))

  const commentMutation = useMutation({
    mutationFn: (text: string) => addOrderComment(id!, text),
    onSuccess: () => {
      setComment('')
      void queryClient.invalidateQueries({ queryKey: ['order-history', id] })
    },
  })

  if (isLoading) return <div className="p-8 text-sm text-ink-2">Lade Auftrag…</div>
  if (isError || !order) {
    return (
      <div className="p-8">
        <p className="text-sm text-coral">
          {error instanceof ApiError ? error.message : 'Auftrag nicht gefunden'}
        </p>
        <button
          onClick={() => zurListe()}
          className="mt-3 text-sm text-primary hover:underline"
        >
          ← Zurück
        </button>
      </div>
    )
  }

  const lines = order.lines ?? []
  const billing = order.addresses?.billing ?? null
  const shipping = order.addresses?.shipping ?? null
  // Kunde = Kaeufer (Rechnung); bei Geschenklieferung steht der Empfaenger nur in der Lieferadresse.
  const nameAddr = billing ?? shipping
  const customerName = nameAddr
    ? [nameAddr.firstname, nameAddr.lastname].filter(Boolean).join(' ')
    : (order.customer?.email ?? '')

  const subtotal = lines.reduce((s, l) => s + Number(l.price) * Number(l.quantity), 0)
  const tax = lines.reduce(
    (s, l) => s + (Number(l.price) * Number(l.quantity) * Number(l.tax)) / (1 + Number(l.tax)),
    0,
  )
  const empfaenger =
    billing && shipping && namensSchluessel(billing) !== namensSchluessel(shipping)
      ? [shipping.firstname, shipping.lastname].filter(Boolean).join(' ') || shipping.company || ''
      : ''
  // Rabatt-/Korrekturzeilen ohne Lagerartikel zählen nicht als Position.
  const artikelPositionen = lines.filter((l) => l.stueckliste?.status !== 'kein_lagerartikel').length
  const summe = order.stueckliste_summe
  const stuecklisteText = summe?.teile.length
    ? `laut Stückliste: ${summe.teile.map((t) => `${fmtQty(t.menge)} × ${t.merchant_sku}`).join(', ')}`
    : ''
  const paid = Number(order.paid_amount)
  const invoice = Number(order.invoice_amount ?? 0)
  const paidInFull = Number.isFinite(paid) && paid > 0 && Number.isFinite(invoice) && paid >= invoice
  const art = order.auftrags_art
  const artHinweise = [
    art?.storniert && 'Storniert',
    art?.fba && 'Versand durch Amazon',
    art?.gutschrift && 'Gutschrift – Mengen sind Stücklistenwerte, keine Rückbuchung',
  ].filter((h): h is string => !!h)
  const trackingLink = httpsLink(order.shipping?.links?.[0])
  const mitgliedsname =
    order.customer?.username && order.customer.username !== order.external_order_id ? order.customer.username : undefined
  const paymentMethod = order.payment_method_id
    ? (PAYMENT_METHODS[String(order.payment_method_id)] ?? String(order.payment_method_id))
    : undefined
  const versand = [order.shipping?.method, order.shipping?.deliverer].filter(Boolean).join(' · ')

  const eigeneNotizen = [
    { label: 'Packhinweis', text: order.pack_infos },
    { label: null, text: order.infos },
    { label: 'Intern', text: order.hidden_infos },
  ].filter((n) => !!n.text?.trim())
  const kundenKommentar = order.customer?.comment?.trim() ?? ''
  const gleicheAdresse =
    !!billing && !!shipping && addressLines(billing).join('|') === addressLines(shipping).join('|')
  const lager = lagerQuery.data ?? []

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as DetailTab)}
      className="px-4 py-3 space-y-3"
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <button
          onClick={() => zurListe()}
          className="inline-flex items-center gap-1 text-xs text-ink-2 hover:text-ink"
        >
          <ChevronLeft className="size-3.5" />
          Aufträge
        </button>
        <PortalBadge portal={order.platform} />
        <h1 className="font-display font-bold text-xl text-ink leading-tight">
          Bestellung <span className="select-all">{drNumber(order.number)}</span>
          {customerName && <span className="text-ink-2 font-normal"> · {customerName}</span>}
        </h1>
        {empfaenger && <span className="text-sm text-ink-2">→ Empfänger: {empfaenger}</span>}
        <OrderStatusFlags status={order.status} />
        <div className="ml-auto flex items-center gap-2">
          <TabsList>
            <TabsTrigger value="auftrag">Auftrag</TabsTrigger>
            <TabsTrigger value="rohdaten">Rohdaten</TabsTrigger>
          </TabsList>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.print()}>
            <Printer className="size-3.5" />
            Drucken
          </Button>
        </div>
      </header>

      <TabsContent value="auftrag" className="mt-0 space-y-3">
        {/* 1. Kopffelder wie DreamRobot: Portal, A-Nr., Re-Nr., Daten, Versender, Paket-Ident */}
        <section
          aria-label="Auftragskopf"
          className="flex flex-wrap gap-x-3 gap-y-2 rounded-md border border-line bg-panel px-3 py-2 [&>div>div:last-child]:whitespace-nowrap"
        >
          <Feld label="Portal" className="flex-1 min-w-20">{order.platform ?? undefined}</Feld>
          <Feld label="A-Nr." mono kopieren className="flex-1 min-w-28">{drNumber(order.number)}</Feld>
          <Feld label="Re-Nr." mono kopieren className="flex-1 min-w-40">{order.invoice_number ?? undefined}</Feld>
          <Feld label="A-Datum" className="flex-1 min-w-36">{fmtDate(order.date)}</Feld>
          <Feld label="Zahl-Datum" className="flex-1 min-w-24">{fmtTag(order.status.is_paid_at)}</Feld>
          <Feld label="Lief-Datum" className="flex-1 min-w-24">{fmtTag(order.shipping?.date ?? order.status.is_shipped_at)}</Feld>
          <Feld label="Versender" className="flex-1 min-w-20">{order.shipping?.deliverer ?? undefined}</Feld>
          <Feld label="P-Ident" mono className="flex-[2] min-w-24">
            {order.shipping?.code && trackingLink ? (
              <a href={trackingLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                {order.shipping.code}
              </a>
            ) : (order.shipping?.code ?? undefined)}
          </Feld>
          <Feld label="Portal-Nr." mono kopieren className="flex-[2] min-w-44">{order.external_order_id ?? undefined}</Feld>
        </section>

        {/* 2. Positionen: Anz., Artikel, Portal-SKU, Lager-Nr. (Sternnummer), Preise — darunter Versand und Summen */}
        <section aria-label="Gekauft" className="rounded-md border-2 border-primary/40 bg-panel overflow-hidden">
          <header className="flex items-baseline gap-3 px-3 py-1.5 border-b border-line-2">
            <h2 className="font-display font-semibold text-sm text-ink">Artikel</h2>
            <span className="text-xs text-ink-2">
              {artikelPositionen} Position{artikelPositionen !== 1 ? 'en' : ''}
              {stuecklisteText && ` · ${stuecklisteText}`}
              {summe && !summe.vollstaendig && ' (unvollständig)'}
            </span>
          </header>
          {artHinweise.length > 0 && (
            <div className="flex flex-wrap gap-2 px-3 py-1 border-b border-line-2 bg-coral/10 text-[12px] font-semibold text-ink">
              {artHinweise.map((h) => <span key={h}>{h}</span>)}
            </div>
          )}
          {lines.length === 0 ? (
            <p className="px-3 py-2 text-sm text-ink-2">Keine Positionen.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-muted border-b border-line-2">
                    <th className="text-right font-medium py-1 pl-3 pr-3">Anz.</th>
                    <th className="text-left font-medium py-1 pr-3">Artikel-Name</th>
                    <th className="text-left font-medium py-1 pr-3">Portal-SKU</th>
                    <th className="text-left font-medium py-1 pr-3">Laut Stückliste</th>
                    <th className="text-right font-medium py-1 pr-3">Ez-Preis</th>
                    <th className="text-right font-medium py-1 pr-3">Gesamt</th>
                    <th className="text-right font-medium py-1 pr-3">MwSt.</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const lagerSku = bildSku(l) ?? null
                    const bild = photosBySku.get(lagerSku ?? '')
                    const teile = l.stueckliste?.teile ?? []
                    const hatBild = !!bild?.primary || !!bild?.components.length
                    const portal = l.sku ? portalLink(order.platform, order.external_order_id, l.sku) : null
                    return (
                      <tr key={l.id} className="border-b border-line-2 last:border-0 align-middle">
                        <td className="py-1.5 pl-3 pr-3 text-right whitespace-nowrap">
                          <span className="font-display font-bold text-base text-ink">{fmtQty(l.quantity)}×</span>
                        </td>
                        <td className="py-1.5 pr-3">
                          <div className="flex items-center gap-2">
                            {photos.data?.enabled !== false && hatBild && (
                              <ProductPhoto images={bild} alt={l.name ?? lagerSku ?? 'Artikel'} compact />
                            )}
                            <span className="text-ink leading-snug">{l.name}</span>
                          </div>
                        </td>
                        {/* SKU des Portal-Angebots (z. B. Amazon-Seller-SKU), vom Anleger frei vergeben: sieht oft
                            wie eine Sternnummer aus, die Lagernummer steht aber nur unter „Laut Stückliste“. */}
                        <td className="py-1.5 pr-3 font-mono text-[12px] text-ink-2 whitespace-nowrap">
                          {l.sku ? (
                            <span className="inline-flex items-center gap-1">
                              {portal ? (
                                <a
                                  href={portal.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`${portal.title} · SKU des Angebots im Portal, keine Lagernummer`}
                                  className="text-primary hover:underline"
                                >
                                  {l.sku}
                                </a>
                              ) : (
                                <span className="select-all" title="SKU des Angebots im Portal, keine Lagernummer">{l.sku}</span>
                              )}
                              <KopierKnopf text={l.sku} label={`Portal-SKU ${l.sku}`} />
                            </span>
                          ) : '—'}
                        </td>
                        <td className="py-1.5 pr-3">
                          {teile.length === 0 ? (
                            <span className="text-muted">—</span>
                          ) : (
                            <ul className="space-y-0.5">
                              {teile.map((t, i) => (
                                <li key={`${t.kopf_id ?? 'x'}-${i}`} title={t.name ?? undefined} className="leading-tight">
                                  <span className="whitespace-nowrap">
                                    <span className="font-display font-bold text-base text-ink">{fmtQty(t.menge)} ×</span>{' '}
                                    {t.merchant_sku ? (
                                      <>
                                        <GrundartikelLink className="font-display font-bold text-base select-all" code={t.merchant_sku} />{' '}
                                        <KopierKnopf text={t.merchant_sku} label={`Sternnummer ${t.merchant_sku}`} />
                                      </>
                                    ) : (
                                      <span className="text-coral font-semibold">{t.grund ? STUECKLISTEN_GRUND[t.grund] : 'Sternnummer fehlt'}</span>
                                    )}
                                  </span>
                                  {t.merchant_sku && t.grund && (
                                    <span className="ml-2 text-[11px] text-coral">{STUECKLISTEN_GRUND[t.grund]}</span>
                                  )}
                                  {t.name && <div className="text-[11px] text-ink-2">{t.name}</div>}
                                </li>
                              ))}
                            </ul>
                          )}
                          {l.lager_sku && (
                            <div className="font-mono text-[11px] text-ink-2 select-all">{l.lager_sku}</div>
                          )}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono">{fmtEUR(l.price)}</td>
                        <td className="py-1.5 pr-3 text-right font-mono font-semibold">
                          {fmtEUR(Number(l.price) * Number(l.quantity))}
                        </td>
                        <td className="py-1.5 pr-3 text-right font-mono text-ink-2">
                          {(Number(l.tax) * 100).toFixed(0)} %
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 border-t border-line-2 bg-panel-2/40 px-3 py-2 text-[13px]">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              <dt className="text-ink-2">Versand</dt>
              <dd className="text-ink">{versand || '—'}</dd>
              {paymentMethod && (
                <>
                  <dt className="text-ink-2">Zahlart</dt>
                  <dd className="text-ink">{paymentMethod}</dd>
                </>
              )}
              {order.shipping?.platform_status && (
                <>
                  <dt className="text-ink-2">Portal-Status</dt>
                  <dd className="text-ink">{order.shipping.platform_status}</dd>
                </>
              )}
              {order.shipping?.amazon_fba && (
                <>
                  <dt className="text-ink-2">Amazon FBA</dt>
                  <dd className="text-ink">Ja</dd>
                </>
              )}
            </dl>
            <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-right">
              <dt className="text-ink-2">Betrag</dt>
              <dd className="font-mono">{fmtEUR(subtotal)}</dd>
              <dt className="text-ink-2">Versand</dt>
              <dd className="font-mono">{fmtEUR(order.shipping?.fee)}</dd>
              <dt className="text-ink-2">darin MwSt. Positionen</dt>
              <dd className="font-mono">{fmtEUR(tax)}</dd>
              <dt className="font-semibold text-ink">Gesamt</dt>
              <dd className="font-mono font-semibold bg-primary/10 rounded px-1.5">
                {fmtEUR(order.invoice_amount)} {order.currency_id && order.currency_id !== 'EUR' ? order.currency_id : ''}
              </dd>
              <dt className="text-ink-2">Bezahlt</dt>
              <dd className={`font-mono font-semibold ${paidInFull ? 'text-success' : 'text-coral'}`}>
                {fmtEUR(order.paid_amount)}
              </dd>
            </dl>
          </div>
        </section>

        {/* 3. Links Kunde mit Rechnungs- und Lieferadresse nebeneinander, daneben Infos und Kommentare,
            rechts der Status — so steht alles Wichtige ohne Scrollen auf einer Seite */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_240px] gap-3 items-start">
          <Block title="Kunde" className="@container">
            {/* Kontaktdaten gehoeren dem Kunden, nicht einer der beiden Adressen; Spalten nach Blockbreite, nicht Fensterbreite */}
            <div className="grid grid-cols-2 @lg:grid-cols-6 gap-x-3 gap-y-2 mb-2">
              {mitgliedsname && <Feld label="Mitgliedsname" kopieren className="min-w-0 col-span-2">{mitgliedsname}</Feld>}
              <Feld label="E-Mail" kopieren className="min-w-0 col-span-2 @lg:col-span-4">{order.customer?.email ?? undefined}</Feld>
              <Feld label="Telefon" kopieren className="min-w-0 col-span-1 @lg:col-span-2">{order.customer?.phone_1 ?? undefined}</Feld>
              <Feld label="Telefon 2" kopieren className="min-w-0 col-span-1 @lg:col-span-2">{order.customer?.phone_2 ?? undefined}</Feld>
              <Feld label="Fax" kopieren className="min-w-0 col-span-1">{order.customer?.fax ?? undefined}</Feld>
              <Feld label="USt-Id" kopieren className="min-w-0 col-span-1">{order.customer?.vat_id ?? undefined}</Feld>
            </div>
            <table className="w-full text-[13px] leading-snug">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-muted">
                  <th className="w-20" />
                  <th className="text-left font-medium pb-0.5 pr-2">
                    <span className="inline-flex items-center gap-1">
                      Rechnungsadresse
                      {billing && <KopierKnopf text={adresseAlsText(billing)} label="Rechnungsadresse" />}
                    </span>
                  </th>
                  <th className="text-left font-medium pb-0.5">
                    <span className="inline-flex items-center gap-1">
                      Lieferadresse
                      {shipping && <KopierKnopf text={adresseAlsText(shipping)} label="Lieferadresse" />}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ADRESS_ZEILEN.map((z) => {
                  const r = billing ? z.wert(billing) : ''
                  const l = shipping ? z.wert(shipping) : ''
                  if (!r && !l) return null
                  return (
                    <tr key={z.label} className="border-t border-line-2 align-top">
                      <th scope="row" className="text-left font-normal text-ink-2 py-0.5 pr-2 whitespace-nowrap">{z.label}</th>
                      <AdressZelle wert={r} label={`Rechnungsadresse ${z.label}`} fett={z.label === 'Name'} className="pr-2" />
                      <AdressZelle wert={l} label={`Lieferadresse ${z.label}`} fett={z.label === 'Name'} />
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {gleicheAdresse && <p className="mt-1 text-[11px] text-muted">Rechnungs- und Lieferadresse sind gleich.</p>}
            {!billing && shipping && <p className="mt-1 text-[11px] text-muted">Keine eigene Rechnungsadresse.</p>}
          </Block>

          <div className="space-y-3">
            <Block title="Infos (eigene Notizen)">
              <Textkasten leer={eigeneNotizen.length === 0}>
                {eigeneNotizen.map((n, i) => (
                  <div key={i}>
                    {n.label && <span className="font-semibold">{n.label}: </span>}
                    {n.text}
                  </div>
                ))}
              </Textkasten>
            </Block>
            <Block title="Kommentare (vom Kunden)">
              <Textkasten leer={!kundenKommentar}>{kundenKommentar}</Textkasten>
            </Block>
          </div>

          <Block title="Status">
            <ul className="space-y-0.5 text-[13px] leading-snug">
              {STATUS_FLAGS.map((f) => {
                const checked = !!order.status[f.key]
                const ts = order.status[f.atKey]
                return (
                  <li
                    key={String(f.key)}
                    className="flex items-center gap-2 min-w-0"
                    title={checked && typeof ts === 'string' ? fmtStatus(ts) : undefined}
                  >
                    <span className={checked ? 'text-ink' : 'text-ink-2'}>{f.label}</span>
                    <span
                      className={`ml-auto tabular-nums ${
                        checked ? (f.danger ? 'font-semibold text-coral' : 'font-semibold text-success') : 'text-muted'
                      }`}
                    >
                      {checked ? (typeof ts === 'string' ? fmtStatus(ts) : 'Ja') : 'Nein'}
                    </span>
                  </li>
                )
              })}
            </ul>
          </Block>
        </div>

        {/* 4. Auftrags-History über die volle Breite */}
        <Panel title="Auftrags-History" subtitle={`${history?.length ?? 0} Einträge`}>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Kommentar hinzufügen…"
                rows={1}
                className="resize-y min-h-9 flex-1"
              />
              <Button
                size="sm"
                onClick={() => comment.trim() && commentMutation.mutate(comment.trim())}
                disabled={!comment.trim() || commentMutation.isPending}
              >
                Kommentar speichern
              </Button>
            </div>
            {commentMutation.isError && (
              <p className="text-xs text-coral">
                {commentMutation.error instanceof ApiError
                  ? commentMutation.error.message
                  : 'Kommentar wurde nicht gespeichert.'}
              </p>
            )}

            {historyQuery.isError ? (
              <p className="text-sm text-coral py-2">Historie konnte nicht geladen werden.</p>
            ) : (history ?? []).length === 0 ? (
              <p className="text-sm text-ink-2 py-2 text-center">Noch keine Einträge.</p>
            ) : (
              <ul className="divide-y divide-line-2">
                {history?.map((entry) => (
                  <li key={entry.id} className="grid grid-cols-[130px_80px_1fr] gap-3 py-1">
                    <div className="font-mono text-xs text-muted">{fmtDate(entry.created_at)}</div>
                    <div>
                      <span className="inline-block px-1.5 py-0.5 rounded bg-panel-2 text-[10px] text-ink-2 font-medium">
                        {HISTORY_TYPES[entry.type] ?? entry.type}
                      </span>
                    </div>
                    <div className="text-[13px] text-ink whitespace-pre-line">
                      {entry.message}
                      {entry.actor_name && <span className="ml-2 text-[11px] text-muted">{entry.actor_name}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>

        {/* 5. Lager-History: welche Lagernummer wann um wie viel bewegt wurde */}
        <Block title="Lager-History">
          {lagerQuery.isLoading ? (
            <p className="text-[13px] text-ink-2">Lade Lagerbuchungen…</p>
          ) : lagerQuery.isError ? (
            <p className="text-[13px] text-coral">Lager-History konnte nicht geladen werden.</p>
          ) : lager.length === 0 ? (
            <p className="text-[13px] text-ink-2">Noch keine Lagerbuchungen vorhanden; der Import kann verzögert sein.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-muted border-b border-line-2">
                    <th className="text-left font-medium py-1 pr-3">Zeit</th>
                    <th className="text-left font-medium py-1 pr-3">Benutzer</th>
                    <th className="text-left font-medium py-1 pr-3">Lager-Nr.</th>
                    <th className="text-left font-medium py-1 pr-3">Artikel</th>
                    <th className="text-left font-medium py-1 pr-3">Kanal</th>
                    <th className="text-right font-medium py-1">Lagerbewegung</th>
                  </tr>
                </thead>
                <tbody>
                  {lager.map((h) => (
                    <tr key={h.id} className="border-b border-line-2 last:border-0">
                      <td className="py-1 pr-3 font-mono text-xs text-muted whitespace-nowrap">{fmtDate(h.created_at)}</td>
                      <td className="py-1 pr-3">{h.user}</td>
                      <td className="py-1 pr-3 whitespace-nowrap">
                        <GrundartikelLink className="font-semibold select-all" code={h.merchant_sku} />
                        {h.stock_sku && <span className="ml-2 font-mono text-[11px] text-ink-2 select-all">{h.stock_sku}</span>}
                      </td>
                      <td className="py-1 pr-3 text-ink-2">{h.name ?? '—'}</td>
                      <td className="py-1 pr-3 text-ink-2">{h.channel ?? '—'}</td>
                      <td className="py-1 text-right font-mono whitespace-nowrap">
                        {h.quantity_before} {h.quantity < 0 ? '−' : '+'} {Math.abs(h.quantity)} ={' '}
                        <span className="font-semibold">{h.quantity_after}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Block>
      </TabsContent>

      <TabsContent value="rohdaten">
        <Panel
          title="Alle Felder (Roh-Dump)"
          subtitle="stahlia_platform.orders + lines + addresses · vollständiger Datensatz"
        >
          <pre className="font-mono text-[11px] leading-relaxed text-ink-2 overflow-auto max-h-[600px] bg-panel-2 p-4 rounded border border-line">
            {JSON.stringify(order, null, 2)}
          </pre>
          <div className="mt-2 flex items-center gap-3 text-xs text-muted">
            <span>Erstellt: {fmtDate(order.created_at)}</span>
            <span>·</span>
            <span>Geändert: {fmtDate(order.updated_at)}</span>
          </div>
        </Panel>
      </TabsContent>
    </Tabs>
  )
}
